import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineFolder,
  HiOutlineXMark,
  HiOutlineCheckCircle
} from 'react-icons/hi2'

const DocumentGroupModal = ({ isOpen, onClose, onSuccess, editGroup = null }) => {
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()

  const [formData, setFormData] = useState({ name: '', description: '' })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const isEditMode = !!editGroup

  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        setFormData({
          name: editGroup.name || '',
          description: editGroup.description || ''
        })
      } else {
        setFormData({ name: '', description: '' })
      }
      setErrors({})
    }
  }, [isOpen, editGroup])

  if (!isOpen) return null

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      setLoading(true)

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || ''
      }

      const params = isWorkspaceSwitched && ownerAccountId
        ? { owner_account_id: ownerAccountId }
        : {}

      let response
      if (isEditMode) {
        response = await apiService.put(`/document-groups/${editGroup.id}/`, payload, { params })
      } else {
        response = await apiService.post('/document-groups/', payload, { params })
      }

      if (response.success) {
        showSuccess(`${getEntity('document').singular} group ${isEditMode ? 'updated' : 'created'} successfully`)
        onSuccess?.()
      } else {
        if (response.error?.data) {
          setErrors(response.error.data)
        } else {
          showError(response.error?.message || `Failed to ${isEditMode ? 'update' : 'create'} group`)
        }
      }
    } catch (error) {
      console.error('Error saving group:', error)
      showError(`Failed to ${isEditMode ? 'update' : 'create'} group`)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({ name: '', description: '' })
    setErrors({})
    setLoading(false)
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all sm:w-full sm:max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <HiOutlineFolder className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isEditMode ? `Edit ${getEntity('document').singular} Group` : `Create ${getEntity('document').singular} Group`}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {isEditMode ? 'Update group information' : `Organize ${getEntity('document').plural.toLowerCase()} into groups`}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Group Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <HiOutlineFolder className="w-4 h-4 text-gray-500" />
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors ${
                    errors.name
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  placeholder="Enter group name"
                  disabled={loading}
                  autoFocus
                />
                {errors.name && (
                  <p className="mt-1.5 text-sm text-danger">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors hover:border-gray-300 resize-none"
                  placeholder="Optional description for this group"
                  rows={3}
                  disabled={loading}
                />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <HiOutlineCheckCircle className="w-5 h-5" />
                      {isEditMode ? 'Update Group' : 'Create Group'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default DocumentGroupModal
